import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { addDays, format, isSunday } from "date-fns";
import SEOHead from "./SEOHead";
import { appointmentBookingSchema } from "../data/seoSchemas";
import { api } from "../services/api";
import "./BookAppointment.css";

const TIME_SLOTS = [
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "02:00 PM",
  "02:30 PM",
  "03:00 PM",
  "03:30 PM",
];

const BookAppointment = () => {
  const [patientName, setPatientName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedDate, setSelectedDate] = useState(addDays(new Date(), 1));
  const [selectedSlot, setSelectedSlot] = useState("10:00 AM");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Convert 12-hour AM/PM string to 24-hour HH:mm:00 string
  const convertTo24Hour = (timeStr) => {
    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":");
    if (hours === "12") {
      hours = "00";
    }
    if (modifier === "PM") {
      hours = parseInt(hours, 10) + 12;
    }
    return `${String(hours).padStart(2, "0")}:${minutes}:00`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    // Validate phone number
    const cleanPhone = phoneNumber.replace(/[\s-]/g, "");
    if (!/^\+?[0-9]{10,14}$/.test(cleanPhone)) {
      setErrorMsg("Please enter a valid 10-digit mobile number.");
      return;
    }

    setSubmitting(true);

    const payload = {
      patient_name: patientName.trim(),
      contact_number: cleanPhone,
      appointment_date: format(selectedDate, "yyyy-MM-dd"), // Correct local date format!
      appointment_time: convertTo24Hour(selectedSlot),
      reason: reason.trim() || "Consultation for joint pain / orthopedic care",
    };

    try {
      const response = await api.createAppointment(payload);
      setConfirmation({
        name: patientName,
        date: format(selectedDate, "EEEE, MMMM d, yyyy"),
        time: selectedSlot,
        id: response.id || "APP-" + Math.floor(1000 + Math.random() * 9000),
      });
      // Reset fields
      setPatientName("");
      setPhoneNumber("");
      setReason("");
    } catch (err) {
      setErrorMsg(err.message || "Failed to confirm appointment request. Please try again or call the clinic.");
    } finally {
      setSubmitting(false);
    }
  };

  // Filter out Sundays (clinic closed)
  const isWeekday = (date) => {
    return !isSunday(date);
  };

  return (
    <section className="booking-section section-padding">
      <SEOHead
        title="Book Appointment with Dr. Sunil Apsingi | Orthopedic Surgeon HITEC City"
        description="Schedule an in-person clinical appointment with Dr. Sunil Apsingi at Medicover Out Patient Center, HITEC City, Hyderabad. Direct slot booking for joint, knee, and shoulder consultations."
        canonicalPath="/bookappointment"
        schemaJson={appointmentBookingSchema}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Book Appointment", path: "/bookappointment" },
        ]}
      />
      <div className="container">
        <div className="text-center mb-5">
          <span className="section-header-pill">
            <i className="fa-solid fa-hospital-user me-1"></i> Fast & Direct Booking
          </span>
          <h1 className="section-title-large">Schedule Your Consultation</h1>
          <p className="section-subtitle-muted">
            Book an in-person clinical consultation with Dr. Sunil Apsingi at Medicover Out Patient Center, HITEC City.
          </p>
        </div>

        <div className="row g-4 justify-content-center align-items-stretch">
          {/* Left Column: Doctor Profile Summary & Clinic Details */}
          <div className="col-lg-5 col-md-12">
            <div className="booking-sidebar-card h-100 p-4 p-lg-5">
              <div className="doctor-badge-row d-flex align-items-center gap-3 mb-4">
                <img
                  src="/images/2022-05-13.png"
                  alt="Dr. Sunil Apsingi"
                  className="booking-doc-thumb"
                />
                <div>
                  <h4 className="mb-0 text-white">Dr. Sunil Apsingi</h4>
                  <small className="text-teal font-monospace">MS Ortho (PGI Chandigarh)</small>
                  <p className="mb-0 text-white-50 small">Chief Joint Replacement Surgeon</p>
                </div>
              </div>

              <div className="clinic-meta-list">
                <div className="meta-row">
                  <div className="meta-icon-circle">
                    <i className="fa-solid fa-hospital"></i>
                  </div>
                  <div>
                    <h6 className="text-white mb-1">Clinic Center</h6>
                    <p className="text-white-50 small mb-0">
                      Medicover Out Patient Center, HUDA Techno Enclave, HITEC City, Hyderabad
                    </p>
                  </div>
                </div>

                <div className="meta-row">
                  <div className="meta-icon-circle">
                    <i className="fa-solid fa-calendar-days"></i>
                  </div>
                  <div>
                    <h6 className="text-white mb-1">Consultation Days</h6>
                    <p className="text-white-50 small mb-0">
                      Monday to Saturday (Closed on Sundays)
                    </p>
                  </div>
                </div>

                <div className="meta-row">
                  <div className="meta-icon-circle">
                    <i className="fa-solid fa-clock"></i>
                  </div>
                  <div>
                    <h6 className="text-white mb-1">Clinic Timings</h6>
                    <p className="text-white-50 small mb-0">
                      10:00 AM to 04:00 PM
                    </p>
                  </div>
                </div>

                <div className="meta-row">
                  <div className="meta-icon-circle">
                    <i className="fa-solid fa-phone"></i>
                  </div>
                  <div>
                    <h6 className="text-white mb-1">Emergency / Inquiries</h6>
                    <p className="text-white-50 small mb-0">
                      +91 8341555765 / 040-68334455
                    </p>
                  </div>
                </div>
              </div>

              <div className="sidebar-reassurance mt-4 p-3 rounded bg-white-10">
                <small className="text-white-50 d-block">
                  <i className="fa-solid fa-shield-halved text-teal me-2"></i>
                  Strict patient privacy. Your medical details are strictly confidential.
                </small>
              </div>
            </div>
          </div>

          {/* Right Column: Interactive Form */}
          <div className="col-lg-7 col-md-12">
            <div className="booking-form-card p-4 p-lg-5 h-100">
              {confirmation ? (
                <div className="booking-success-container text-center py-4">
                  <div className="success-icon-badge mb-3">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <h3 className="text-navy mb-2">Appointment Request Submitted!</h3>
                  <p className="text-muted mb-4">
                    Thank you, <strong>{confirmation.name}</strong>. Our hospital coordination desk will call you shortly to confirm your token.
                  </p>

                  <div className="confirmation-summary p-4 mb-4 text-start">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Booking Reference:</span>
                      <strong className="text-navy">#{confirmation.id}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Consultation Date:</span>
                      <strong className="text-navy">{confirmation.date}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Preferred Time:</span>
                      <strong className="text-navy">{confirmation.time}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Hospital:</span>
                      <strong className="text-navy">Medicover OPC, HITEC City</strong>
                    </div>
                  </div>

                  <button
                    className="btn-medical-secondary"
                    onClick={() => setConfirmation(null)}
                  >
                    <i className="fa-solid fa-plus me-2"></i> Book Another Appointment
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <h3 className="form-title mb-4">Patient Information</h3>

                  {errorMsg && (
                    <div className="alert alert-danger py-2 px-3 mb-4 d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-exclamation"></i>
                      <span>{errorMsg}</span>
                    </div>
                  )}

                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label htmlFor="patientName" className="medical-label">
                        Patient Full Name *
                      </label>
                      <input
                        type="text"
                        id="patientName"
                        className="medical-input"
                        placeholder="e.g. Ramesh Kumar"
                        value={patientName}
                        onChange={(e) => setPatientName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="phoneNumber" className="medical-label">
                        Contact Number *
                      </label>
                      <input
                        type="tel"
                        id="phoneNumber"
                        className="medical-input"
                        placeholder="e.g. 9876543210"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="medical-label">
                        Select Date *
                      </label>
                      <DatePicker
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        filterDate={isWeekday}
                        minDate={new Date()}
                        maxDate={addDays(new Date(), 45)}
                        dateFormat="EEEE, MMMM d, yyyy"
                        className="medical-input cursor-pointer"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="medical-label">
                        Preferred Time Slot *
                      </label>
                      <div className="slot-grid">
                        {TIME_SLOTS.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            className={`slot-chip ${selectedSlot === slot ? "selected" : ""}`}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="reason" className="medical-label">
                      Primary Condition / Symptoms
                    </label>
                    <textarea
                      id="reason"
                      rows={3}
                      className="medical-input"
                      placeholder="e.g. Knee pain while walking, ACL sports injury, shoulder stiffness..."
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="btn-medical-primary w-100 py-3"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin me-2"></i> Submitting Request...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-calendar-check me-2"></i> Confirm Appointment Request
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookAppointment;